// React
import { useCallback, useState } from 'react'

// React Router Dom
import { useHistory } from 'react-router-dom'

// Utils
import { oidc_userManager } from 'utils'
import { messageUtils_error } from 'utils/common/message.utils'

// Constant
import { AUTH_URL, HOME_URL } from 'constant/url.constant'

// Custom Hook
import { useAuth } from 'hooks'

const useOidcHook = () => {
  // Hook
  const history = useHistory()
  const { auth_setToken, auth_logout } = useAuth()
  const [loading, setLoading] = useState<{ isSignInLoading: boolean }>({ isSignInLoading: false })

  /**
   * @description Handle loading
   *
   * @param {string} type
   * @param {boolean} value
   *
   * @return {void} void
   */
  const handleLoading = useCallback((type: 'isSignInLoading', value: boolean): void => {
    setLoading(prevLoading => ({ ...prevLoading, [type]: value }))
  }, [])

  /**
   * @description Sign in to oidc
   *
   * @return {Promise<void>} Promise<void>
   */
  const onSignIn = useCallback(async (): Promise<void> => {
    handleLoading('isSignInLoading', true)

    try {
      await oidc_userManager().signinRedirect()
    } catch (err) {
      if (err instanceof Error) messageUtils_error(err.message)
    } finally {
      handleLoading('isSignInLoading', false)
    }
  }, [handleLoading])

  /**
   * @description Validate the sign in redirect from signinRedirect
   *
   * @param {string} uri
   *
   * @return {Promise<void>} Promise<void>
   */
  const onSignInValidate = useCallback(
    async (uri?: string): Promise<void> => {
      try {
        const { access_token: accessToken, refresh_token: refreshToken } = await oidc_userManager().signinRedirectCallback(
          uri || window.location.href,
        )

        auth_setToken({ accessToken, refreshToken: refreshToken || '' })

        history.replace(HOME_URL.INDEX)

        window.location.reload()
      } catch (err) {
        if (err instanceof Error) messageUtils_error(err.message)

        history.replace(AUTH_URL.LOGIN)
      }
    },
    [history, auth_setToken],
  )

  /**
   * @description Validate the sign in silent redirect from signinRedirect
   *
   * @param {string} uri
   *
   * @return {Promise<void>} Promise<void>
   */
  const onSignInSilentValidate = useCallback(async (uri?: string): Promise<void> => {
    try {
      await oidc_userManager(true).signinSilentCallback(uri || window.location.href)
    } catch (err) {
      if (err instanceof Error) messageUtils_error(err.message)
    }
  }, [])

  /**
   * @description Validate the sign out redirect from signoutRedirect
   *
   * @param {string} uri
   *
   * @return {Promise<void>} Promise<void>
   */
  const onSignOutValidate = useCallback(
    async (uri?: string): Promise<void> => {
      try {
        await oidc_userManager().signoutRedirectCallback(uri || window.location.href)

        auth_logout()
      } catch (err) {
        if (err instanceof Error) messageUtils_error(err.message)
      } finally {
        history.replace(HOME_URL.INDEX)

        window.location.reload()
      }
    },
    [history, auth_logout],
  )

  return { loading, onSignIn, onSignInSilentValidate, onSignInValidate, onSignOutValidate }
}

export { useOidcHook }
